import { 
	getProduct,
	formatMoney,
	addToCartMultiple,
} from './_global-functions.js';

if(customElements.get("product-card") === undefined) {
	class ProductCard extends HTMLElement {
		constructor() {
			super();

			this.product;
			this.productId;
			this.metafields;
			this.productData = {};
		}

		async connectedCallback() {
			// Get metafields if passed via data attribute
			if (this.dataset.metafields) {
				try {
					this.metafields = JSON.parse(this.dataset.metafields);
				} catch (error) {
					console.warn('Failed to parse metafields data:', error);
					this.metafields = null;
				}
			}

			// Check what data attributes we have
			const hasAllRequiredData = this.checkRequiredDataAttributes();
			
			if (hasAllRequiredData) {
				// Use data attributes directly
				this.productData = {
					handle: this.dataset.productHandle,
					url: this.dataset.productUrl,
					title: this.dataset.productTitle,
					subtitle: this.dataset.productSubtitle,
					price: parseFloat(this.dataset.productPrice),
					featured_image: this.dataset.productImage
				};
			} else {
				// Fetch product data and merge with available data attributes
				this.product = await getProduct(this.dataset.productHandle);
				
				if(!this.product) {
					console.error(`Product card not found for handle: ${this.dataset.productHandle}`);
					return;
				}

				// Merge product data with any available data attributes
				this.productData = {
					handle: this.dataset.productHandle || this.product.handle,
					url: this.dataset.productUrl || this.product.url,
					title: this.dataset.productTitle || this.product.title,
					subtitle: this.dataset.productSubtitle || this.product.type,
					price: this.dataset.productPrice ? parseFloat(this.dataset.productPrice) : this.product.price,
					featured_image: this.dataset.productImage || this.product.featured_image
				};
			}

			this.render();
		}

		checkRequiredDataAttributes() {
			const requiredAttributes = [
				'productHandle',
				'productUrl', 
				'productTitle',
				'productSubtitle',
				'productPrice',
				'productImage'
			];

			return requiredAttributes.every(attr => 
				this.dataset[attr] && this.dataset[attr].trim() !== ''
			);
		}

		capitalizeFirstLetter(string) {
			return string.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
		}

		render() {
			// Use metafields if available, otherwise fallback to productData
			const productUrl = this.getProductUrl();
			const productImage = this.getProductImage();
			const productTitle = this.getProductTitle();
			const productSubtitle = this.getProductSubtitle();
			const productPrice = this.getProductPrice();

			// <p>${this.capitalizeFirstLetter(productSubtitle)}</p>
		this.innerHTML = `
			<div class="swiper-slide-image">
				<a href="${productUrl}">
					<img src="${productImage}" alt="${productTitle}" width="94">
				</a>
			</div>
			<a href="${productUrl}" class="swiper-slide-content">
				<h3>${this.capitalizeFirstLetter(productTitle)}</h3>
				
				<p>${formatMoney(productPrice)}</p>
			</a>
			<dynamic-atc data-product-handle="${this.productData.handle}"></dynamic-atc>
		`;
		}

		getProductUrl() {
			// Check for cart redirect metafield first
			if (this.metafields && this.metafields['cart.cart_redirect']?.value) {
				let url = this.metafields['cart.cart_redirect'].value;
				// Add locale prefix if needed (same logic as in _cart.js)
				if (typeof Shopify !== 'undefined' && Shopify.country !== 'US' && !url.includes('en-')) {
					url = `/en-${Shopify.country}${url}`;
				}
				return url;
			}
			// Fallback to productData (from data attributes or product fetch)
			return this.productData.url;
		}

		getProductImage() {
			// Check for main image metafield first
			if (this.metafields && this.metafields['prod_globals.main_image']?.value) {
				// Handle both direct URL and object with image property
				const imageValue = this.metafields['prod_globals.main_image'].value;
				if (typeof imageValue === 'object' && imageValue.image) {
					return imageValue.image;
				} else if (typeof imageValue === 'string') {
					return imageValue;
				}
			}
			// Fallback to productData (from data attributes or product fetch)
			return this.productData.featured_image;
		}

		getProductTitle() {
			// Check for custom title metafield first
			if (this.metafields && this.metafields['cart.title']?.value) {
				return this.capitalizeFirstLetter(this.metafields['cart.title'].value);
			}
			// Fallback to productData (from data attributes or product fetch)
			return this.productData.title;
		}

		getProductSubtitle() {
			// Check for custom subtitle metafield first
			if (this.metafields && this.metafields['cart.subtitle']?.value) {
				return this.capitalizeFirstLetter(this.metafields['cart.subtitle'].value);
			}
			// Fallback to productData (from data attributes or product fetch)
			return this.productData.subtitle;
		}

		getProductPrice() {
			// Metafields don't typically override price, so use productData
			return this.productData.price;
		}
	}

	customElements.define("product-card", ProductCard);
}
